import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import { FunctionSource } from '../../core/api/models/function.model';
import { PagedResult } from '../../core/api/models/pagination.model';
import { UserImportChangeType, UserImportRecord } from '../../core/api/models/user-import.model';
import { UserImportsApiService } from '../../core/api/services/user-imports-api.service';
import { localDateInputToUtcRange } from '../../core/date/local-day-range';
import {
  PaginationChange,
  PaginationComponent,
} from '../../shared/ui/pagination/pagination.component';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../shared/ui/pagination/pagination.config';

interface UserImportFilters {
  email: string;
  source: FunctionSource | '';
  changeType: UserImportChangeType | '';
  fromDate: string;
  toDate: string;
}

@Component({
  selector: 'app-user-imports',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, PaginationComponent],
  templateUrl: './user-imports.component.html',
  styleUrl: './user-imports.component.scss',
})
export class UserImportsComponent implements OnInit {
  private readonly api = inject(UserImportsApiService);

  readonly sources: FunctionSource[] = ['Daas', 'AppDirect', 'Webshop', 'ServiceNow'];
  readonly changeTypes: UserImportChangeType[] = ['Created', 'Updated', 'Deleted'];
  data: PagedResult<UserImportRecord> | null = null;
  paging: PaginationChange = { page: DEFAULT_PAGE, pageSize: DEFAULT_PAGE_SIZE };
  filters: UserImportFilters = this.emptyFilters();
  loading = false;
  error = '';

  ngOnInit(): void {
    this.load();
  }

  applyFilters(): void {
    this.paging = { ...this.paging, page: DEFAULT_PAGE };
    this.load();
  }

  clearFilters(): void {
    this.filters = this.emptyFilters();
    this.applyFilters();
  }

  load(): void {
    const dateRange = localDateInputToUtcRange(this.filters.fromDate, this.filters.toDate);
    this.loading = true;
    this.error = '';
    this.api.getUserImports(this.paging.page, this.paging.pageSize, {
      email: this.filters.email.trim() || undefined,
      source: this.filters.source || undefined,
      changeType: this.filters.changeType || undefined,
      fromUtc: dateRange.fromUtc,
      toUtc: dateRange.toUtc,
    }).pipe(finalize(() => this.loading = false)).subscribe({
      next: value => {
        this.data = value;
        this.paging = { page: value.page, pageSize: value.pageSize };
      },
      error: response => this.error = this.validationMessage(response, 'Could not load user imports.'),
    });
  }

  changePagination(paging: PaginationChange): void {
    this.paging = paging;
    this.load();
  }

  roleChange(user: UserImportRecord): string {
    const oldRoles = user.oldRoles.length ? user.oldRoles.join(', ') : '—';
    const roles = user.roles.length ? user.roles.join(', ') : '—';
    return `${oldRoles} → ${roles}`;
  }

  private emptyFilters(): UserImportFilters {
    return {
      email: '',
      source: '',
      changeType: '',
      fromDate: '',
      toDate: '',
    };
  }

  private validationMessage(response: any, fallback: string): string {
    const errors = response?.error?.errors as Record<string, string[]> | undefined;
    return (errors ? Object.values(errors).flat()[0] : undefined)
      || response?.error?.message
      || response?.error?.title
      || fallback;
  }
}
